if (!customElements.get('email-popup')) {
  class EmailPopup extends HTMLElement {
    constructor() {
      super();
      this.cookieName = 'email_popup_suppress';
      this.opened = false;
      this.timer = null;
      this.scrollHandler = null;
      this.handleEscape = this.handleEscape.bind(this);
    }

    connectedCallback() {
      if (this.shouldSkip()) return;

      this.bindUI();

      if (this.dataset.editorPreview === 'true') {
        this.open();
        return;
      }

      this.scheduleTrigger();
    }

    disconnectedCallback() {
      this.clearTriggers();
      document.removeEventListener('keydown', this.handleEscape);
      document.documentElement.style.overflow = '';
    }

    shouldSkip() {
      const path = window.location.pathname || '';
      if (path.startsWith('/checkouts/') || path.startsWith('/checkout')) return true;

      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      if (isMobile && this.dataset.hideMobile === 'true') return true;
      if (!isMobile && this.dataset.hideDesktop === 'true') return true;

      if (this.dataset.editorPreview !== 'true' && this.getCookie(this.cookieName)) return true;

      if (!this.dataset.klaviyoCompany || !this.dataset.klaviyoList) return true;

      return false;
    }

    bindUI() {
      this.form = this.querySelector('[data-popup-form]');
      this.input = this.form ? this.form.querySelector('input[type="email"]') : null;
      this.errorEl = this.querySelector('[data-popup-error]');
      this.submitBtn = this.querySelector('[data-popup-submit]');
      this.ctaText = this.querySelector('[data-popup-cta-text]');
      this.loadingEl = this.querySelector('[data-popup-loading]');
      this.defaultState = this.querySelector('[data-popup-state="default"]');
      this.successState = this.querySelector('[data-popup-state="success"]');

      this.querySelectorAll('[data-popup-close]').forEach((el) => {
        el.addEventListener('click', () => this.close('close'));
      });

      const overlay = this.querySelector('[data-popup-overlay]');
      if (overlay) overlay.addEventListener('click', () => this.close('close'));

      if (this.form) this.form.addEventListener('submit', (e) => this.handleSubmit(e));
      if (this.input) this.input.addEventListener('input', () => this.clearError());
    }

    scheduleTrigger() {
      const trigger = this.dataset.trigger || 'both';
      const delay = parseInt(this.dataset.delay, 10);
      const scrollTarget = parseInt(this.dataset.scroll, 10);

      if ((trigger === 'time' || trigger === 'both') && delay >= 0) {
        this.timer = window.setTimeout(() => this.open(), delay * 1000);
      }

      if ((trigger === 'scroll' || trigger === 'both') && scrollTarget > 0) {
        let ticking = false;
        this.scrollHandler = () => {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(() => {
            const doc = document.documentElement;
            const scrollable = doc.scrollHeight - window.innerHeight;
            // A page with nothing to scroll is 0% scrolled, not 100%. The old
            // fallback of 100 meant any scroll event on a short page counted as
            // a full read and opened the popup straight away.
            const pct = scrollable > 0 ? ((window.scrollY || doc.scrollTop) / scrollable) * 100 : 0;
            if (pct >= scrollTarget) this.open();
            ticking = false;
          });
        };
        window.addEventListener('scroll', this.scrollHandler, { passive: true });
      }
    }

    clearTriggers() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
        this.scrollHandler = null;
      }
    }

    open() {
      if (this.opened) return;
      this.opened = true;
      this.clearTriggers();
      this.removeAttribute('hidden');
      window.requestAnimationFrame(() => this.classList.add('is-open'));
      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('keydown', this.handleEscape);
      window.setTimeout(() => {
        if (this.input) this.input.focus({ preventScroll: true });
      }, 350);
      this.dispatchEvent(new CustomEvent('email-popup:open', { bubbles: true }));
    }

    close(reason) {
      if (!this.opened) return;
      this.opened = false;
      this.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      document.removeEventListener('keydown', this.handleEscape);
      window.setTimeout(() => this.setAttribute('hidden', ''), 350);

      const days = parseInt(
        reason === 'submit' ? this.dataset.suppressSubmit : this.dataset.suppressClose,
        10
      ) || 30;
      this.setCookie(this.cookieName, '1', days);
    }

    handleEscape(e) {
      if (e.key === 'Escape') this.close('close');
    }

    handleSubmit(e) {
      e.preventDefault();
      this.clearError();

      const email = (this.input && this.input.value || '').trim();
      if (!this.isValidEmail(email)) {
        this.showError();
        if (this.input) this.input.focus();
        return;
      }

      this.setLoading(true);

      this.subscribe(email)
        .then(() => this.showSuccess())
        .catch((err) => {
          if (window.console && console.error) console.error('[email-popup] Klaviyo subscribe error', err);
          this.showError(err && err.userMessage ? err.userMessage : null);
        })
        .then(() => this.setLoading(false));
    }

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    showError(msg) {
      if (this.input) this.input.classList.add('is-error');
      if (this.errorEl) {
        if (msg) this.errorEl.textContent = msg;
        this.errorEl.removeAttribute('hidden');
      }
    }

    clearError() {
      if (this.input) this.input.classList.remove('is-error');
      if (this.errorEl) this.errorEl.setAttribute('hidden', '');
    }

    setLoading(loading) {
      if (!this.submitBtn) return;
      this.submitBtn.disabled = loading;
      if (this.ctaText) this.ctaText.toggleAttribute('hidden', loading);
      if (this.loadingEl) this.loadingEl.toggleAttribute('hidden', !loading);
    }

    showSuccess() {
      if (this.defaultState) this.defaultState.setAttribute('hidden', '');
      if (this.successState) this.successState.removeAttribute('hidden');

      const days = parseInt(this.dataset.suppressSubmit, 10) || 180;
      this.setCookie(this.cookieName, '1', days);

      this.dispatchEvent(new CustomEvent('email-popup:subscribed', { bubbles: true }));

      const auto = parseInt(this.dataset.autoClose, 10);
      if (auto > 0) {
        window.setTimeout(() => this.close('submit'), auto * 1000);
      }
    }

    subscribe(email) {
      const company = this.dataset.klaviyoCompany;
      const listId = this.dataset.klaviyoList;
      const url = 'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(company);

      const body = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Email Popup',
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email }
              }
            }
          },
          relationships: {
            list: { data: { type: 'list', id: listId } }
          }
        }
      };

      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': '2024-10-15'
        },
        body: JSON.stringify(body)
      }).then((res) => {
        if (res.ok || res.status === 202) return;
        return res.text().then((text) => {
          const err = new Error('Klaviyo subscription failed: ' + res.status + ' ' + text);
          err.userMessage = 'Something went wrong. Please try again.';
          throw err;
        });
      });
    }

    getCookie(name) {
      const match = document.cookie.split('; ').find((c) => c.indexOf(name + '=') === 0);
      return match ? match.split('=')[1] : null;
    }

    setCookie(name, value, days) {
      const d = new Date();
      d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
      document.cookie = name + '=' + value + '; expires=' + d.toUTCString() + '; path=/; SameSite=Lax';
    }
  }

  customElements.define('email-popup', EmailPopup);
}
